import { DefaultPrivacyLevel, datadogRum } from "@datadog/browser-rum";
import { PropsWithChildren, useEffect, useState } from "react";
import { APP_CONFIG, SERVICE_NAME } from "@/constants/config.constants";
import {
  DATADOG_APP_HOSTNAME,
  DATADOG_APPLICATION_ID,
  DATADOG_TOKEN,
} from "@/constants/environment.constants";
import { getEnv, isDev } from "@/helpers/environment/environment.helpers";
import { useAuthenticationState } from "@/hooks/useAuthenticationState/useAuthenticationState";

// We want to init DatadogRum before any other routes.
export function DatadogRumInit({ children }: PropsWithChildren) {
  const isInitialized = useDatadogRumInit();

  if (!isInitialized) {
    return null;
  }

  return children;
}

export function useDatadogRumInit() {
  const {
    authState: { decodedToken },
  } = useAuthenticationState();
  const [isInitialized, setIsInitialized] = useState(false);

  const { "custom:company_id": companyId, "custom:user_id": userId } =
    decodedToken ?? {
      "custom:company_id": "",
      "custom:user_id": "",
    };

  useEffect(() => {
    if (isInitialized) return;

    if (isDev()) {
      setIsInitialized(true);
      return;
    }

    datadogRum.init({
      allowedTracingUrls: [DATADOG_APP_HOSTNAME],
      applicationId: DATADOG_APPLICATION_ID,
      clientToken: DATADOG_TOKEN,
      site: "datadoghq.com",
      service: SERVICE_NAME,
      env: getEnv(),
      version: APP_CONFIG.release,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 10, // RUM Without Limits: Only record 10% of sessions
      traceSampleRate: 20,
      trackUserInteractions: true,
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
    });
    datadogRum.setUser({
      id: userId,
      name: `company_id_${companyId}`,
    });
    datadogRum.setGlobalContextProperty("user_session", {
      environment: APP_CONFIG.environment,
      release: APP_CONFIG.release,
      service: SERVICE_NAME,
    });
    datadogRum.setGlobalContextProperty("owner", "conversation-intelligence");
    setIsInitialized(true);

    return () => {
      datadogRum.removeGlobalContextProperty("user_session");
      datadogRum.removeGlobalContextProperty("owner");
      datadogRum.clearUser();
    };
  }, []);

  return isInitialized;
}
